import React from "react";
import { useAppTheme } from "../../theme/AppThemeContext.jsx";
import {
  Box,
  Popover,
  Paper,
  Typography,
  IconButton,
  ButtonBase,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DailyRepeatOptions } from './DailyRepeatOptions.jsx';
import { WeeklyRepeatOptions } from './WeeklyRepeatOptions.jsx';
import { MonthlyRepeatOptions } from './MonthlyRepeatOptions.jsx';

/**
 * RepeatPickerPopover (Daily + Weekly + Monthly)
 * ---------------------------------------------
 * Modular approach with independent components for each frequency type
 */

export function RepeatPickerPopover({
  value,
  onChange,
  open,
  anchorEl,
  onClose,
  DatePickerComponent,
}) {
  const { tokens } = useAppTheme();
  const textDark = "rgb(20, 20, 20)";
  const muted = "rgba(0,0,0,0.45)";
  const border = "1px solid rgba(0,0,0,0.08)";

  const options = [
    { key: "once", label: "Once" },
    { key: "daily", label: "Daily" },
    { key: "weekly", label: "Weekly" },
    { key: "monthly", label: "Monthly" },
  ];

  const freq = value.frequency ?? "once";
  const isExpanded = freq === "daily" || freq === "weekly" || freq === "monthly";

  // Date-picker anchors
  const [startAnchor, setStartAnchor] = React.useState(null);
  const [endAnchor, setEndAnchor] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      setStartAnchor(null);
      setEndAnchor(null);
    }
  }, [open]);

  function setFrequency(next) {
    if (next === "once") {
      onChange({
        frequency: "once",
        interval: undefined,
        byWeekday: undefined,
        startDate: null,
        endDate: null,
      });
      return;
    }

    const startDate = value.startDate ?? new Date();
    const base = {
      ...value,
      frequency: next,
      startDate,
      interval: clampInt(value.interval ?? 1, 1, 365),
    };

    // Defaults per mode
    if (next === "daily") {
      base.byWeekday = undefined;
    }

    if (next === "weekly") {
      if (!Array.isArray(base.byWeekday) || base.byWeekday.length === 0) {
        base.byWeekday = [startDate.getDay()];
      }
    }

    if (next === "monthly") {
      base.byWeekday = undefined;
    }

    onChange(base);
  }

  const startLabel = value.startDate ? formatPretty(value.startDate) : "Select start";
  const endLabel = value.endDate ? formatPretty(value.endDate) : "Set End Date";
  const isIndefinite = freq !== "once" && !value.endDate;

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1,
            borderRadius: 4,
            border,
            boxShadow: "0px 10px 28px rgba(0,0,0,0.14)",
            overflow: "hidden",
          },
        }}
      >
        <Paper sx={{ width: 390, background: "white", borderRadius: 4 }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 2, pb: 1.25 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: textDark }}>
              Repeat
            </Typography>

            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close repeat picker"
              sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                border,
                background: "rgba(0,0,0,0.03)",
                "&:hover": { background: "rgba(0,0,0,0.05)" },
              }}
            >
              <CloseIcon fontSize="small" sx={{ color: muted }} />
            </IconButton>
          </Box>

          {/* Segmented pills */}
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Box
              sx={{
                borderRadius: 999,
                border,
                background: "white",
                padding: "6px",
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "6px",
              }}
            >
              {options.map((opt) => {
                const active = opt.key === freq;
                return (
                  <ButtonBase
                    key={opt.key}
                    onClick={() => setFrequency(opt.key)}
                    sx={{
                      height: 34,
                      borderRadius: 999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: active ? "rgba(244,159,153,0.75)" : "transparent",
                      border: active ? "1px solid rgba(244,159,153,0.55)" : "1px solid transparent",
                      transition: "background 120ms ease, border 120ms ease",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 900, color: active ? "white" : muted }}>
                      {opt.label}
                    </Typography>
                  </ButtonBase>
                );
              })}
            </Box>
          </Box>

          {/* Expanded: Daily / Weekly / Monthly */}
          {isExpanded && (
            <Box sx={{ px: 2, pb: 2 }}>
              <Divider sx={{ mb: 1.5, borderColor: "rgba(0,0,0,0.06)" }} />

              {/* Frequency-specific options */}
              {freq === "daily" && (
                <DailyRepeatOptions
                  value={value}
                  onChange={onChange}
                  coral={tokens.primary}
                />
              )}

              {freq === "weekly" && (
                <WeeklyRepeatOptions
                  value={value}
                  onChange={onChange}
                  coral={tokens.primary}
                />
              )}

              {freq === "monthly" && (
                <MonthlyRepeatOptions
                  value={value}
                  onChange={onChange}
                  coral={tokens.primary}
                />
              )}

              {/* Date rows */}
              <DateRangeOptions
                value={value}
                onChange={onChange}
                DatePickerComponent={DatePickerComponent}
                startAnchor={startAnchor}
                setStartAnchor={setStartAnchor}
                endAnchor={endAnchor}
                setEndAnchor={setEndAnchor}
                startLabel={startLabel}
                endLabel={endLabel}
                isIndefinite={isIndefinite}
              />
            </Box>
          )}
        </Paper>
      </Popover>

      {/* Start date picker */}
      <DatePickerComponent
        title="Start Date"
        value={value.startDate ?? new Date()}
        open={Boolean(startAnchor)}
        anchorEl={startAnchor}
        onClose={() => setStartAnchor(null)}
        onChange={(d) => {
          const end = value.endDate && d > value.endDate ? null : value.endDate ?? null;
          onChange({ ...value, startDate: d, endDate: end });
          setStartAnchor(null);
        }}
      />

      {/* End date picker */}
      <DatePickerComponent
        title="End Date"
        value={value.endDate ?? value.startDate ?? new Date()}
        open={Boolean(endAnchor)}
        anchorEl={endAnchor}
        onClose={() => setEndAnchor(null)}
        onChange={(d) => {
          const start = value.startDate ?? new Date();
          const safeEnd = d < start ? start : d;
          onChange({ ...value, startDate: start, endDate: safeEnd });
          setEndAnchor(null);
        }}
      />
    </>
  );
}

/* ---------------- Date Range Component ---------------- */

function DateRangeOptions({
  value,
  onChange,
  DatePickerComponent,
  startAnchor,
  setStartAnchor,
  endAnchor,
  setEndAnchor,
  startLabel,
  endLabel,
  isIndefinite,
}) {
  const textDark = "rgb(20, 20, 20)";
  const rowBorder = "1px solid rgba(0,0,0,0.12)";

  return (
    <>
      {/* Start row */}
      <Box
        sx={{
          border: rowBorder,
          borderRadius: 999,
          px: 1.5,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.0,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 900, color: textDark }}>
          Start
        </Typography>

        <ButtonBase
          onClick={(e) => setStartAnchor(e.currentTarget)}
          sx={{
            height: 34,
            px: 1.25,
            borderRadius: 999,
            background: "rgba(0,0,0,0.04)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 120,
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>
            {startLabel}
          </Typography>
        </ButtonBase>
      </Box>

      {/* End row */}
      <ButtonBase
        onClick={(e) => setEndAnchor(e.currentTarget)}
        sx={{
          width: "fit-content",
          border: rowBorder,
          borderRadius: 999,
          px: 2,
          py: 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.0,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>
          {value.endDate ? `End: ${endLabel}` : "Set End Date"}
        </Typography>
      </ButtonBase>

      <Typography sx={{ fontSize: 12, fontWeight: 800, color: "rgba(0,0,0,0.35)" }}>
        {isIndefinite ? "This task repeats indefinitely." : "This task has an end date."}
      </Typography>
    </>
  );
}

/* ---------------- helpers ---------------- */

function clampInt(n, min, max) {
  return Math.max(min, Math.min(max, Math.floor(n)));
}

// "9. Feb 2026" style
function formatPretty(d) {
  if (!d) return "Select date";
  
  // Convert to Date object if it's not already
  const date = d instanceof Date ? d : new Date(d);
  
  // Check if it's a valid date
  if (isNaN(date.getTime())) return "Invalid date";
  
  const day = date.getDate();
  const mon = date.toLocaleString(undefined, { month: "short" });
  const yr = date.getFullYear();
  return `${day}. ${mon} ${yr}`;
}
